import {Component, inject, input, OnInit, viewChild} from '@angular/core';
import {TranslocoDirective} from '@jsverse/transloco';
import {LucideAngularModule} from 'lucide-angular';
import {Meta, Title} from '@angular/platform-browser';
import {DOCUMENT} from '@angular/common';
import {RoutingService} from '@core/service/routing.service';
import {VenuePipe} from '@domain/venue/pipe/venue.pipe';
import {ActivityApi} from '@domain/activity/activity.api';
import {NotificationService} from '@shared/notification/notification.service';
import {ActivityDTO} from '@domain/activity/activity.dto';
import {ActivityUtil} from '@domain/activity/activity.util';
import {RouteGenerator} from '@core/route-generator';
import {UI_ROUTES} from '@core/ui.routes';
import {
  ActivityAboutComponent
} from '@domain/activity/component/activity-display/activity-about/activity-about.component';
import {
  ActivitySchedulesComponent
} from '@domain/activity/component/activity-display/activity-schedules/activity-schedules.component';
import {
  ActivityPriceComponent
} from '@domain/activity/component/activity-display/activity-price/activity-price.component';
import {
  ActivityLinksComponent
} from '@domain/activity/component/activity-display/activity-links/activity-links.component';
import {ActivityHostComponent} from '@domain/activity/component/activity-display/activity-host/activity-host.component';

@Component({
  selector: 'app-activity-display',
  providers: [
    VenuePipe
  ],
  imports: [
    TranslocoDirective,
    LucideAngularModule,
    ActivityAboutComponent,
    ActivitySchedulesComponent,
    ActivityPriceComponent,
    ActivityLinksComponent,
    ActivityHostComponent
  ],
  templateUrl: './activity-display.component.html',
  styleUrl: './activity-display.component.scss'
})
export class ActivityDisplayComponent implements OnInit {

  public readonly isPreview = input.required<boolean>();
  public readonly hostUrl = input.required<string>();
  public readonly activity = input.required<ActivityDTO>();

  public readonly activityHostComponent = viewChild.required<ActivityHostComponent>('activityHost');

  private readonly activityApi = inject(ActivityApi);
  private readonly notifyService = inject(NotificationService);
  private readonly routingService = inject(RoutingService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  public readonly routes = {
    home: RouteGenerator.abs(UI_ROUTES.public.dashboard),
    search: RouteGenerator.abs(UI_ROUTES.public.activity.search)
  };

  public mediaUrl: string | null = null;
  public thumbUrl: string | null = null;

  public ngOnInit(): void {
    const activity = this.activity();
    if (!activity) {
      this.notifyService.toast({
        message: `Cette activité n'existe pas ou n'est plus publiée.`
      });
      this.routingService.navigate([this.routes.home]).then(_ => {});
      return;
    }

    this.mediaUrl = this.activityApi.getCoverUrl(activity.id);
    this.thumbUrl = this.activityApi.getThumbnailUrl(activity.id);
    let seoTitle = activity.title ?? '';
    // const seoVenue = this.activityVenuePipe.transform(activity, false);
    // if (seoVenue) {
    //   seoTitle += ` à ${seoVenue}`;
    // }
    this.title.setTitle(seoTitle);
    this.meta.updateTag({ name: 'description', content: activity.description ?? '' });
    this.meta.updateTag({ property: 'og:title', content: seoTitle });
    this.meta.updateTag({ property: 'og:description', content: activity.description ?? '' });
    if (activity.cover) {
      this.meta.updateTag({ property: 'og:image', content: this.mediaUrl });
    }
    const canonicalUrl = this.getUrl();
    if (!canonicalUrl) {
      return;
    }
    this.setCanonicalURL(canonicalUrl);
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl});
    this.meta.updateTag({ property: 'og:type', content: 'article' });

  }

  private setCanonicalURL(url: string) {
    const link: HTMLLinkElement = this.document.querySelector("link[rel='canonical']") ?? this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    this.document.head.appendChild(link);
  }

  public toggleShowContact(): void {
    this.activityHostComponent().toggleShowContact();
  }

  private getUrl(): string | null {
    const slug = ActivityUtil.getSlug(this.activity());
    if (!slug) {
      return null;
    }
    return RouteGenerator.uiUrl(UI_ROUTES.public.activity.home, {slug: slug});
  }

}
